import { useEffect, useState, FC } from 'react'
import { toast, ToastContent } from 'react-toastify'
import { getAvailableFulfillmentCenterAccess } from 'src/services/fulfillmentCenter/fulfillmentCenterApi'
import {
  removeFulfillmentCenterSelected,
  setFulfillmentCenterSelected as setFulfillmentCenterSelectedService,
} from 'src/services/fulfillmentCenter/fulfillmentCenterService'
import logo from '../../assets/logo/kdabra-light.svg'
import LoginBarcode from 'src/components/Login/LoginBarcode'
import GoogleLoginPage from 'src/components/security/GoogleLoginPage'
import { ssoClient, stockClient } from 'src/services/SigninSetup'
import Loading from './components/loading'
import SelectFulfillmentCenter from './components/selectFulfillmentCenter'
import { useDispatch } from 'react-redux'
import { actions } from 'src/features/state'
import http from 'src/infrastructure/http'

interface FulfillmentCenterSelected {
  name: string
  id: number
  is_market: boolean
}

interface Option {
  label: string
  value: number
  is_market: boolean
}

type LoginStep = 'credentials' | 'fulfillment-center'

const Login: FC<{
  bypassActive: boolean,
  currentUser?: unknown,
  onFulfillmentCenterSelected?: (fulfillmentCenter?: FulfillmentCenterSelected) => void,
}> = ({ bypassActive, currentUser, onFulfillmentCenterSelected }) => {
  const [fulfillmentCenterSelected, setfulfillmentCenterSelected] = useState<FulfillmentCenterSelected>()
  const [options, setOptions] = useState<Option[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [step, setStep] = useState<LoginStep>(currentUser ? 'fulfillment-center' : 'credentials')
  const [hasAttemptedFulfillmentCenterLoad, setHasAttemptedFulfillmentCenterLoad] = useState(false)
  const dispatch = useDispatch()

  const logoutCurrentFlow = () => {
    if (bypassActive) {
      stockClient.handleLogout()
      return
    }

    ssoClient.logout()
  }

  const getToken = async () => {
    try {
      const token = await ssoClient.getToken()
      if (token) {
        dispatch(actions.setToken(token))
      } else {
        logoutCurrentFlow()
      }
    } catch (error) {
      toast.error(error as ToastContent)
    }
  }

  const resetFulfillmentCenterSelection = () => {
    setfulfillmentCenterSelected(undefined)
    setOptions([])
    setHasAttemptedFulfillmentCenterLoad(false)
  }

  const clearCurrentDockIfNeeded = (selectedFulfillmentCenter: FulfillmentCenterSelected) => {
    const dock = localStorage.currentDock && JSON.parse(localStorage.currentDock)
    if (dock && dock.id_fc !== selectedFulfillmentCenter.id) {
      localStorage.removeItem('currentDock')
    }
  }

  const loadFulfillmentCenters = async ({ preserveLoading = false } = {}) => {
    if (!preserveLoading) {
      setLoading(true)
    }

    setHasAttemptedFulfillmentCenterLoad(true)

    try {
      const result = await getAvailableFulfillmentCenterAccess()

      if (!result.isOk) {
        if (result.data.message) {
          toast.error(`Falha no acesso: ${result.data.message}`)
        } else {
          toast.error('Falha ao carregar FCs ativos!')
        }
        return
      }

      setOptions(
        result.data.data.map((fc: FulfillmentCenterSelected) => ({
          label: fc.name,
          value: fc.id,
          is_market: fc.is_market,
        })),
      )
    } finally {
      if (!preserveLoading) {
        setLoading(false)
      }
    }
  }

  const goToFulfillmentCenterStep = async () => {
    setHasAttemptedFulfillmentCenterLoad(true)
    setStep('fulfillment-center')
    await getToken()
    await loadFulfillmentCenters({ preserveLoading: true })
  }

  const getErrorMessage = (error: any, defaultMessage: string) => {
    if (error?.response?.status === 403) {
      return 'Você não tem permissão para acessar o sistema!'
    }

    return error?.response?.data?.message || defaultMessage
  }

  useEffect(() => {
    if (currentUser) {
      setStep('fulfillment-center')
      return
    }

    setStep('credentials')
  }, [currentUser])

  useEffect(() => {
    if (step !== 'fulfillment-center' || hasAttemptedFulfillmentCenterLoad) {
      return
    }

    loadFulfillmentCenters()
  }, [step, hasAttemptedFulfillmentCenterLoad])

  const onLoginComplete = async (idToken: any) => {
    setLoading(true)
    try {
      await ssoClient.login({ token: idToken })
      await goToFulfillmentCenterStep()
    } catch (e: any) {
      removeFulfillmentCenterSelected()
      logoutCurrentFlow()
      toast.error(getErrorMessage(e, 'Não foi possível realizar o login com sua conta google!'))
    } finally {
      setLoading(false)
    }
  }

  const onLoginCompleteWithEmail = async (email: string, password: string) => {
    setLoading(true)

    try {
      await ssoClient.login({
        email,
        password,
      })
      await goToFulfillmentCenterStep()
    } catch (e: any) {
      removeFulfillmentCenterSelected()
      logoutCurrentFlow()
      toast.error(getErrorMessage(e, 'E-mail ou senha inválidos!'))
    } finally {
      setLoading(false)
    }
  }

  const onLoginWithBarcode = async (barcode: string) => {
    setLoading(true)

    try {
      await stockClient.handleLogin(barcode)
      await goToFulfillmentCenterStep()
    } catch (error: any) {
      removeFulfillmentCenterSelected()
      logoutCurrentFlow()
      toast.error(getErrorMessage(error, 'Não foi possível realizar o login com o crachá'))
    } finally {
      setLoading(false)
    }
  }

  const onFulfillmentCenterConfirm = async () => {
    if (!fulfillmentCenterSelected) {
      toast.error('Selecione o FC antes de continuar')
      return
    }

    setLoading(true)

    try {
      setFulfillmentCenterSelectedService(fulfillmentCenterSelected)
      onFulfillmentCenterSelected?.(fulfillmentCenterSelected)

      if (fulfillmentCenterSelected.is_market) {
        await http.get('/v2/auth/validate-market')
      }

      clearCurrentDockIfNeeded(fulfillmentCenterSelected)

      if (bypassActive) {
        stockClient.dispatchLogIn()
      } else {
        ssoClient.dispatchLogIn()
      }
    } catch (error: any) {
      removeFulfillmentCenterSelected()
      onFulfillmentCenterSelected?.(undefined)
      logoutCurrentFlow()
      toast.error(getErrorMessage(error, 'Não foi possível definir o FC para o login!'))
    } finally {
      setLoading(false)
    }
  }

  const onBackToLogin = () => {
    removeFulfillmentCenterSelected()
    onFulfillmentCenterSelected?.(undefined)
    resetFulfillmentCenterSelection()
    setStep('credentials')
    logoutCurrentFlow()
  }

  useEffect(() => {
    if (step === 'fulfillment-center' && localStorage.getItem('shppr:sso:atk[stock]') === null) {
      onBackToLogin()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const getHelpMessage = () => {
    if (step === 'fulfillment-center') {
      return 'Selecione o FC para continuar'
    }

    if (bypassActive) {
      return 'Informe o código do seu crachá'
    }

    return 'Clique no botão abaixo para entrar com a sua conta corporativa'
  }

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <div className='page-wrapper'>
        <div className='auth-bg bg-color6'>
          <div className='authentication-box'>
            <div className='text-center'>
              <img src={logo} alt='' />
            </div>
            <div className='card mt-4'>
              <div className='card-body'>
                <div className='text-center'>
                  <h4>LOGIN CORPORATIVO</h4>
                  <h6>{getHelpMessage()}</h6>
                </div>

                {step === 'credentials'
                  ? (bypassActive ? (
                    <LoginBarcode
                      onLoginWithBarcode={onLoginWithBarcode}
                      submitting={loading}
                    />
                  ) : (
                    <GoogleLoginPage
                      onLoginComplete={onLoginComplete}
                      // eslint-disable-next-line no-console
                      onLoginFail={(e) => console.error(e)}
                      emailEnabled
                      onEmailLoginComplete={onLoginCompleteWithEmail}
                    />
                  ))
                  : (
                    <>
                      <SelectFulfillmentCenter
                        options={options}
                        fulfillmentCenterSelected={fulfillmentCenterSelected}
                        setFulfillmentCenterSelected={setfulfillmentCenterSelected}
                        loading={loading}
                      />

                      <button
                        className='btn btn-primary btn-block mt-3'
                        type='button'
                        onClick={onFulfillmentCenterConfirm}
                        disabled={!fulfillmentCenterSelected}
                      >
                        Continuar com o FC selecionado
                      </button>

                      <button
                        className='btn btn-xs btn-block mt-2'
                        type='button'
                        onClick={() => {
                          loadFulfillmentCenters()
                        }}
                      >
                        Recarregar FCs
                      </button>

                      <button
                        className='btn btn-xs btn-block mt-2'
                        type='button'
                        onClick={onBackToLogin}
                      >
                        Trocar conta
                      </button>
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Login
